import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  computed,
  effect,
  inject,
  signal
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { TuiButtonModule, TuiHostedDropdownModule } from "@taiga-ui/core";
import { TuiCheckboxModule } from "@taiga-ui/kit";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ThemeMode, ThemeSwitcherService } from "../theme-switcher.service";
import { TuiLetModule } from "@taiga-ui/cdk";
import { ActionButtonComponent } from "../../ui/button/action-button.component";
import { ModalHeaderComponent } from "../../ui/modal/modal-header.component";
import { TranslocoModule } from "@ngneat/transloco";
import { Overlay, OverlayConfig } from "@angular/cdk/overlay";
import { BreakpointObserver } from "@angular/cdk/layout";
import { TemplatePortal } from "@angular/cdk/portal";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs";
import { trigger, transition, style, animate } from "@angular/animations";
import { IntercomService } from "../../domain/support/intercom.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "dev-portal-navigation-settings",

  imports: [
    CommonModule,
    TuiHostedDropdownModule,
    TuiButtonModule,
    TuiCheckboxModule,
    ReactiveFormsModule,
    TuiLetModule,
    ActionButtonComponent,
    TranslocoModule,
    ModalHeaderComponent
  ],
  templateUrl: "./navigation-settings.component.html",
  styleUrls: ["./navigation-settings.component.scss"],
  animations: [
    trigger("slideUpDown", [
      transition(":enter", [
        style({ transform: "translateY(100%)" }),
        animate("300ms ease-in", style({ transform: "translateY(0)" }))
      ]),
      transition(":leave", [animate("300ms ease-out", style({ transform: "translateY(100%)" }))])
    ])
  ]
})
export class NavigationSettingsComponent {
  private themeSwitcher = inject(ThemeSwitcherService);
  private overlay: Overlay = inject(Overlay);
  private viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
  private breakpointObserver = inject(BreakpointObserver);
  private intercomService = inject(IntercomService);
  private overlayConfig: OverlayConfig = {
    hasBackdrop: true,
    disposeOnNavigation: true,
    panelClass: "mobile-bottom-sheet-panel-class",
    positionStrategy: this.overlay.position().global().centerHorizontally().bottom()
  };
  isMobile = toSignal(
    this.breakpointObserver.observe("(max-width: 767px)").pipe(map((breakpoint) => breakpoint.matches))
  );
  private overlayRef = this.overlay.create(this.overlayConfig);

  isSettingsMenuOpen = false;
  isSettingsMenuOpenMobile = signal(false);
  currentTheme = this.themeSwitcher.mode;
  settingsType: "theme" | "settings" | "language" = "settings";
  themeForm = new FormGroup({
    selectedTheme: new FormControl()
  });
  @ViewChild("settingsMobile", { static: true, read: TemplateRef })
  settingsMobile!: TemplateRef<never>;

  themeIcon = computed(() => `/assets/icons/${this.currentTheme().toLowerCase()}-theme.svg#img`);

  constructor() {
    this.overlayRef
      .outsidePointerEvents()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.triggerOpenChange(false);
      });

    effect(() => {
      this.themeForm.setValue({ selectedTheme: this.currentTheme() });
    });

    effect(() => {
      if (this.isSettingsMenuOpenMobile() && this.isMobile()) {
        this.intercomService.hide();
        this.overlayRef.attach(new TemplatePortal(this.settingsMobile, this.viewContainerRef));
      } else {
        this.intercomService.show();
        this.overlayRef.detach();
      }
    });
  }

  triggerOpenChange($event: boolean) {
    this.isSettingsMenuOpenMobile.set($event);
    this.isSettingsMenuOpen = $event;
  }

  toggleTheme(mode: ThemeMode) {
    this.themeSwitcher.toggleMode(mode);
    this.backToSettings();
    this.isSettingsMenuOpen = false;
    this.isSettingsMenuOpenMobile.set(false);
  }

  backToSettings() {
    this.settingsType = "settings";
  }
}
